import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, Button, Skeleton } from "@mui/material";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import "./ListBoxes.css";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert any value to a stable string key */
export const toIdKey = (value) => String(value ?? "").trim();

/** Compare two IDs after normalisation */
export const sameId = (left, right) => toIdKey(left) === toIdKey(right);

const customerSiteKey = (cusId, siteId) => `${toIdKey(cusId)}:${toIdKey(siteId)}`;

/**
 * Canonical, order-independent snapshot of assignedSites.
 * Shape accepted: [ { customer: <id|obj>, sites: [<id|obj>, ...] } ]
 */
export const snapshotAssigned = (arr) => {
	if (!Array.isArray(arr) || arr.length === 0) return "";
	return arr
		.map((a) => {
			const cusId = a?.customer?.cus_id != null
				? toIdKey(a.customer.cus_id)
				: toIdKey(a?.customer);
			const siteIds = Array.isArray(a?.sites)
				? a.sites
					.map((s) => (s?.site_id != null ? toIdKey(s.site_id) : toIdKey(s)))
					.sort()
					.join(",")
				: "";
			return `${cusId}:${siteIds}`;
		})
		.sort()
		.join("|");
};

/** Stable ID-content snapshot of the customers array */
const snapshotCustomers = (arr) =>
	(arr || []).map((c) => toIdKey(c?.cus_id)).sort().join(",");

/** Stable ID-content snapshot of the sites array */
const snapshotSites = (arr) =>
	(arr || []).map((s) => customerSiteKey(s?.cus_id, s?.site_id)).sort().join(",");

/**
 * Normalise flat allocation format [ { cus_id, site_id } ]
 * into grouped format            [ { customer: cusId, sites: [siteId] } ]
 */
const normalizePropFormat = (raw) => {
	if (!Array.isArray(raw) || raw.length === 0) return raw;

	const looksFlat = raw.every(
		(e) => e && (e.site_id != null || e.cus_id != null) && !Array.isArray(e.sites)
	);
	if (!looksFlat) return raw;

	const map = new Map();

	raw.forEach((e) => {
		const cusId =
			e?.cus_id ??
			e?.customer?.cus_id ??
			e?.customer;

		const siteId =
			e?.site_id ??
			e?.site?.site_id ??
			e?.site;

		if (cusId == null || siteId == null) return;

		const key = String(cusId);

		if (!map.has(key)) {
			map.set(key, { customer: cusId, sites: [] });
		}

		map.get(key).sites.push(siteId);
	});

	return Array.from(map.values());
};

// ---------------------------------------------------------------------------
// Core builder
// ---------------------------------------------------------------------------
/**
 * Build selectedSites map: { [cusIdKey]: { customer: <obj>, sites: [<obj>] } }
 *
 * THE KEY RULE that was broken before:
 *   Every assigned entry has its OWN customer ID. We must resolve THAT customer
 *   for THAT entry only. We never let one entry's customer bleed into another.
 *
 * Resolution order for the customer object:
 *   1. rawCustomerId is already a full object  → use it (refresh from live map)
 *   2. Look up rawCustomerId in live customers map
 *   3. Look up rawCustomerId in prevCustomers map
 *   4. Look up first matched site's cus_id in live/prev customers map
 *   5. Build a minimal synthetic object from site data
 *   6. Bare-minimum last-resort object
 *
 * Resolution order for each site object:
 *   1. Look up by site_id in live sites map
 *   2. Look up by site_id in prevSites map
 *   (If neither found the site is dropped to avoid phantom entries)
 */
export const buildSelectedSitesFromProp = ({
	assignedSitesProp: rawProp,
	sites = [],
	customers = [],
	previousSelectedSites = {},
}) => {
	const assignedSitesProp = normalizePropFormat(rawProp);
	const result = {}; // { [cusIdKey]: { customer, sites[] } }

	// ── Build live lookup maps ─────────────────────────────────────────────
	const liveCusById = new Map(); // cusIdKey → customer obj
	const liveSiteByPair = new Map();
	const liveSiteById = new Map(); // siteIdKey → site obj

	(customers || []).forEach((c) => {
		if (!c) return;
		const k = toIdKey(c.cus_id);
		if (k) liveCusById.set(k, c);
	});

	(sites || []).forEach((s) => {
		if (!s) return;
		const k = toIdKey(s.site_id);
		const pairKey = customerSiteKey(s.cus_id, s.site_id);
		if (pairKey !== ":") liveSiteByPair.set(pairKey, s);
		if (k && !liveSiteById.has(k)) liveSiteById.set(k, s);
	});

	// ── Build previous-state fallback maps ─────────────────────────────────
	const prevCusById = new Map();
	const prevSiteByPair = new Map();
	const prevSiteById = new Map();

	Object.values(previousSelectedSites || {}).forEach((entry) => {
		if (!entry) return;
		const ck = toIdKey(entry?.customer?.cus_id);
		if (ck) prevCusById.set(ck, entry.customer);
		(entry?.sites || []).forEach((s) => {
			const sk = toIdKey(s?.site_id);
			const pairKey = customerSiteKey(ck || s?.cus_id, s?.site_id);
			if (pairKey !== ":") prevSiteByPair.set(pairKey, s);
			if (sk) prevSiteById.set(sk, s);
		});
	});

	// ── Process each assigned entry independently ──────────────────────────
	(assignedSitesProp || []).forEach((assigned) => {
		// ── 1. Determine this entry's customer ID ──────────────────────────
		const rawCus = assigned?.customer; // may be number | string | object
		const rawCusIdKey = toIdKey(
			rawCus?.cus_id != null ? rawCus.cus_id : rawCus
		);

		// ── 2. Resolve site objects for THIS entry ─────────────────────────
		const rawSiteIds = Array.isArray(assigned?.sites) ? assigned.sites : [];

		const resolvedSites = rawSiteIds
			.map((rawSite) => {
				// already a full site object?
				if (rawSite && typeof rawSite === "object" && rawSite.site_id != null) {
					// prefer fresh live version
					const pairKey = customerSiteKey(rawCusIdKey || rawSite.cus_id, rawSite.site_id);
					return liveSiteByPair.get(pairKey) || prevSiteByPair.get(pairKey) || rawSite;
				}
				const k = toIdKey(rawSite);
				if (!k) return null;
				const pairKey = customerSiteKey(rawCusIdKey, k);
				const exactSite = liveSiteByPair.get(pairKey) || prevSiteByPair.get(pairKey);
				if (exactSite) return exactSite;
				// live first, prev fallback. Only use site_id-only fallback for legacy rows.
				return rawCusIdKey ? null : liveSiteById.get(k) || prevSiteById.get(k) || null;
			})
			.filter(
				(s, idx, arr) =>
					s != null &&
					// deduplicate by site_id
					idx === arr.findIndex((x) => sameId(x?.site_id, s?.site_id))
			);

		// ── 3. Resolve customer object for THIS entry ──────────────────────
		let customer = null;

		// Strategy 1 – rawCus is already a full object
		if (rawCus && typeof rawCus === "object" && rawCus.cus_id != null) {
			customer = liveCusById.get(toIdKey(rawCus.cus_id)) || rawCus;
		}

		// Strategy 2 – look up raw ID in live customers
		if (!customer && rawCusIdKey) {
			customer = liveCusById.get(rawCusIdKey) || null;
		}

		// Strategy 3 – look up raw ID in previous state
		if (!customer && rawCusIdKey) {
			customer = prevCusById.get(rawCusIdKey) || null;
		}

		// Strategy 4 – derive from THIS ENTRY's resolved sites' cus_id
		// IMPORTANT: only use the cus_id from sites that belong to rawCusIdKey.
		// We verify the site's cus_id matches the entry's customer ID before using it.
		if (!customer && resolvedSites.length > 0) {
			for (const site of resolvedSites) {
				const siteCusKey = toIdKey(site?.cus_id);
				if (!siteCusKey) continue;
				// Only use this site's cus_id if it matches the entry's customer id,
				// OR if the entry has no customer id at all (truly unknown).
				if (!rawCusIdKey || sameId(siteCusKey, rawCusIdKey)) {
					customer =
						liveCusById.get(siteCusKey) ||
						prevCusById.get(siteCusKey) ||
						null;
					if (customer) break;
				}
			}
		}

		// Strategy 5 – build synthetic object from first matched site
		if (!customer && resolvedSites.length > 0) {
			const s = resolvedSites[0];
			customer = {
				cus_id: s?.cus_id ?? rawCus,
				customer_name: s?.parent_customer_name || s?.customer_name || "Assigned Customer",
				customer_number: s?.parent_customer_number || s?.customer_number || rawCusIdKey || toIdKey(s?.cus_id),
			};
		}

		// Strategy 6 – bare-minimum object so nothing is silently dropped
		if (!customer && rawCusIdKey) {
			customer = {
				cus_id: rawCus,
				customer_name: `Customer ${rawCusIdKey}`,
				customer_number: rawCusIdKey,
			};
		}

		if (!customer) return; // truly nothing to work with

		// ── 4. Determine the key for the result map ────────────────────────
		// Always key by the ENTRY's raw customer ID (rawCusIdKey), NOT by the
		// resolved customer object's cus_id. This ensures that two entries with
		// different raw customer IDs never get merged into the same bucket, even
		// if Strategy 4/5/6 resolved the same synthetic object for both.
		const bucketKey = rawCusIdKey || toIdKey(customer.cus_id);
		if (!bucketKey) return;

		// If the same customer ID appears in multiple entries (shouldn't happen
		// with well-formed data, but guard anyway) merge their sites.
		if (result[bucketKey]) {
			const existingIds = new Set(result[bucketKey].sites.map((s) => toIdKey(s.site_id)));
			resolvedSites.forEach((s) => {
				if (!existingIds.has(toIdKey(s.site_id))) {
					result[bucketKey].sites.push(s);
				}
			});
		} else {
			result[bucketKey] = { customer, sites: resolvedSites };
		}
	});

	return { selectedSites: result };
};

// ---------------------------------------------------------------------------
// Debounce hook
// ---------------------------------------------------------------------------
const useDebounce = (value, delay = 300) => {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const id = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(id);
	}, [value, delay]);
	return debounced;
};

const truncateWords = (value, maxWords = 6) => {
	const s = String(value || "").trim();
	if (!s) return "";
	const words = s.split(/\s+/);
	return words.length > maxWords ? `${words.slice(0, maxWords).join(" ")}...` : s;
};

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------
const ListboxesSkeleton = () => (
	<div className="user-cus-site-selection">
		{[0, 1, 2, 3].map((col) => (
			<div className="list-box" key={col}>
				{col !== 3 && (
					<Box pb={2}>
						<Skeleton variant="rectangular" height={40} width="100%" sx={{ borderRadius: "10px" }} />
					</Box>
				)}
				<div className="list-box-container" style={col === 3 ? { minHeight: "490px" } : {}}>
					{Array.from({ length: 6 }).map((_, i) => (
						<div className="list-box-item" key={i}>
							<Skeleton width="80%" height={20} sx={{ mb: 1, borderRadius: "10px" }} />
							<Skeleton width="60%" height={16} sx={{ borderRadius: "10px" }} />
						</div>
					))}
				</div>
			</div>
		))}
	</div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const Listboxes = ({
	customers = [],
	sites = [],
	assignedSitesProp = [],
	onAssignedSitesChange,
	multipleCustomersAllowed = true,
	multipleSitesAllowed = true,
}) => {
	const { t } = useTranslation();
	const singleCustomer = !multipleCustomersAllowed;
	const singleSitePerCustomer = !multipleSitesAllowed;

	// ── UI state ──────────────────────────────────────────────────────────────
	const [customerSelected, setCustomerSelected] = useState(null);
	const [customerSearchTerm, setCustomerSearchTerm] = useState("");
	const [sitesSearchTerm, setSitesSearchTerm] = useState("");
	const debouncedCustomerTerm = useDebounce(customerSearchTerm, 400);
	const debouncedSitesTerm = useDebounce(sitesSearchTerm, 400);

	// ── Core allocation state ─────────────────────────────────────────────────
	const [selectedSites, setSelectedSites] = useState({});
	const [siteSelected, setSiteSelected] = useState([]);
	const [selectedSitesToMove, setSelectedSitesToMove] = useState([]);

	// ── Refs ──────────────────────────────────────────────────────────────────

	// Skeleton: flips true once, never back → no re-render cost
	const initializedRef = useRef(false);

	// Latest selectedSites accessible in effects without stale closure
	const selectedSitesRef = useRef(selectedSites);
	useEffect(() => { selectedSitesRef.current = selectedSites; }, [selectedSites]);

	// "Init in progress" guard — breaks the post-save parent↔child notify loop
	const isInitialisingRef = useRef(false);

	// Content-snapshot refs (init side) — separate from notify side
	const lastAssignedSnapshotRef = useRef(null);
	const lastCustomersSnapshotRef = useRef(null);
	const lastSitesSnapshotRef = useRef(null);

	// Notify-side snapshot ref — keeps notify from echoing unchanged data back
	const lastNotifiedSnapshotRef = useRef(null);

	// ── assignedSites: synchronous derived value (useMemo, not useEffect) ─────
	//
	// MUST be useMemo. If it were useEffect+setState, the extra async render
	// would fire the notify effect, call onAssignedSitesChange, the parent
	// would update assignedSitesProp, triggering init again → flicker loop.
	const assignedSites = useMemo(
		() =>
			Object.entries(selectedSites).map(([cusId, entry]) => ({
				customer: entry?.customer?.cus_id ?? cusId,
				sites: (entry?.sites || []).map((s) => s?.site_id).filter(Boolean),
			})),
		[selectedSites]
	);

	useEffect(() => {
		console.log("Assigned Sites Prop:", assignedSitesProp);
	}, [assignedSitesProp]);

	// ── Init effect ───────────────────────────────────────────────────────────
	//
	// Fires when assignedSitesProp, customers, or sites change.
	// Guards use CONTENT snapshots (sorted ID strings), not array references or
	// lengths, so a parent re-render that passes a new array reference with the
	// same data is a no-op.
	useEffect(() => {
		const incomingSnapshot = snapshotAssigned(assignedSitesProp);
		const cusSnapshot = snapshotCustomers(customers);
		const sitSnapshot = snapshotSites(sites);

		if (
			incomingSnapshot === lastAssignedSnapshotRef.current &&
			cusSnapshot === lastCustomersSnapshotRef.current &&
			sitSnapshot === lastSitesSnapshotRef.current
		) return; // nothing meaningful changed

		const propActuallyChanged = incomingSnapshot !== lastAssignedSnapshotRef.current;

		// Commit snapshot refs before setState so re-entrant calls skip cleanly
		lastAssignedSnapshotRef.current = incomingSnapshot;
		lastCustomersSnapshotRef.current = cusSnapshot;
		lastSitesSnapshotRef.current = sitSnapshot;

		// Suppress notify callback for this prop-driven update (not user-driven)
		isInitialisingRef.current = true;

		const { selectedSites: next } = buildSelectedSitesFromProp({
			assignedSitesProp,
			sites,
			customers,
			previousSelectedSites: selectedSitesRef.current,
		});

		setSelectedSites(next);

		// Reset interaction state only when the actual allocation IDs changed.
		// If only the lookup arrays refreshed (same IDs), keep the user's
		// in-progress selection intact.
		if (propActuallyChanged) {
			setSiteSelected([]);
			setSelectedSitesToMove([]);
			setCustomerSelected((prev) => {
				if (!prev) return null;
				return customers.find((c) => sameId(c?.cus_id, prev?.cus_id)) || null;
			});
		}

		initializedRef.current = true; // show real UI, hide skeleton

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assignedSitesProp, customers, sites]);

	// ── Notify effect ─────────────────────────────────────────────────────────
	//
	// Only fires for GENUINE USER CHANGES (adding/removing sites).
	// When isInitialisingRef is true the change came FROM the prop — echoing it
	// back would cause the parent to re-pass assignedSitesProp → loop → flicker.
	useEffect(() => {
		if (!onAssignedSitesChange) return;

		if (isInitialisingRef.current) {
			// Prop-driven update: suppress callback, just record baseline snapshot
			isInitialisingRef.current = false;
			lastNotifiedSnapshotRef.current = snapshotAssigned(assignedSites);
			return;
		}

		const snap = snapshotAssigned(assignedSites);
		if (snap === lastNotifiedSnapshotRef.current) return; // truly unchanged

		lastNotifiedSnapshotRef.current = snap;
		onAssignedSitesChange(assignedSites);
	}, [assignedSites, onAssignedSitesChange]);

	// ── Derived data ──────────────────────────────────────────────────────────

	const customerById = useMemo(() => {
		const map = new Map();
		(customers || []).forEach((c) => {
			if (!c) return;
			const k = toIdKey(c.cus_id);
			if (k) map.set(k, c);
		});
		return map;
	}, [customers]);

	const allAssignedSiteKeys = useMemo(() => {
		const keys = new Set();
		Object.entries(selectedSites).forEach(([cusId, entry]) => {
			(entry?.sites || []).forEach((s) => {
				const key = customerSiteKey(cusId, s?.site_id);
				if (key !== ":") keys.add(key);
			});
		});
		return keys;
	}, [selectedSites]);

	const availableSites = useMemo(() => {
		if (!customerSelected) return [];
		return (sites || []).filter(
			(s) =>
				s &&
				sameId(s.cus_id, customerSelected.cus_id) &&
				!allAssignedSiteKeys.has(customerSiteKey(customerSelected.cus_id, s.site_id))
		);
	}, [customerSelected, sites, allAssignedSiteKeys]);

	const availableSitesFiltered = useMemo(() => {
		const term = debouncedSitesTerm.toLowerCase();
		if (!term) return availableSites;
		return availableSites.filter(
			(s) =>
				s &&
				(s.customer_site_name?.toLowerCase().includes(term) ||
					s.customer_site_number?.toLowerCase().includes(term))
		);
	}, [availableSites, debouncedSitesTerm]);

	const filteredCustomers = useMemo(() => {
		const term = debouncedCustomerTerm.toLowerCase();
		if (!term) return customers;
		return customers.filter(
			(c) =>
				c &&
				(c.customer_name?.toLowerCase().includes(term) ||
					c.customer_id?.toLowerCase().includes(term))
		);
	}, [customers, debouncedCustomerTerm]);

	// Summary used for single-customer / single-site constraint enforcement
	const assignedSummary = useMemo(() => {
		const totalCustomers = assignedSites.length;
		const first = assignedSites[0] || null;
		return {
			totalCustomers,
			firstCustomerId: first?.customer || null,
			totalSitesInFirst: first ? (first.sites || []).length : 0,
		};
	}, [assignedSites]);

	const disableAllSelection =
		singleCustomer &&
		singleSitePerCustomer &&
		assignedSummary.totalCustomers === 1 &&
		assignedSummary.totalSitesInFirst === 1;

	// ── Handlers ──────────────────────────────────────────────────────────────

	const handleCustomerListItemClick = useCallback(
		(item) => {
			if (!item) return;
			if (
				singleCustomer &&
				assignedSummary.totalCustomers >= 1 &&
				!sameId(item.cus_id, assignedSummary.firstCustomerId)
			) return;
			setCustomerSelected(item);
			setSiteSelected([]);
		},
		[singleCustomer, assignedSummary]
	);

	const handleSiteListItemClick = useCallback(
		(item) => {
			if (!item) return;
			if (singleSitePerCustomer) {
				const custId = toIdKey(customerSelected?.cus_id);
				if (!custId) return;
				const alreadyCount =
					(selectedSites[custId]?.sites || []).length + siteSelected.length;
				if (alreadyCount >= 1 && !siteSelected.includes(item)) return;
			}
			setSiteSelected((prev) =>
				prev.includes(item) ? prev.filter((s) => s !== item) : [...prev, item]
			);
		},
		[singleSitePerCustomer, customerSelected, selectedSites, siteSelected]
	);

	const handleSelectedSiteClick = useCallback((site) => {
		if (!site) return;
		setSelectedSitesToMove((prev) =>
			prev.includes(site) ? prev.filter((s) => s !== site) : [...prev, site]
		);
	}, []);

	const handleMoveToSelectedSites = useCallback(() => {
		if (!customerSelected || siteSelected.length === 0) return;
		if (
			singleCustomer &&
			assignedSummary.totalCustomers >= 1 &&
			!sameId(customerSelected.cus_id, assignedSummary.firstCustomerId)
		) return;

		let sitesToAdd = [...siteSelected];
		if (singleSitePerCustomer) {
			const custId = toIdKey(customerSelected.cus_id);
			if ((selectedSites[custId]?.sites || []).length >= 1) sitesToAdd = [];
			else sitesToAdd = sitesToAdd.slice(0, 1);
		}
		if (sitesToAdd.length === 0) return;

		setSelectedSites((prev) => {
			const next = { ...prev };
			const key = toIdKey(customerSelected.cus_id);
			const existing = next[key]?.sites || [];
			const trulyNew = sitesToAdd.filter(
				(s) => !existing.some((e) => sameId(e.site_id, s.site_id))
			);
			if (trulyNew.length === 0) return prev;
			next[key] = { customer: customerSelected, sites: [...existing, ...trulyNew] };
			return next;
		});

		setSiteSelected([]);
	}, [customerSelected, siteSelected, singleCustomer, singleSitePerCustomer, assignedSummary, selectedSites]);

	const handleMoveToFilteredSites = useCallback(() => {
		if (selectedSitesToMove.length === 0) return;

		const toRemove = new Set(
			selectedSitesToMove.map((s) => customerSiteKey(s?.cus_id, s?.site_id)).filter((key) => key !== ":")
		);

		setSelectedSites((prev) => {
			const next = { ...prev };
			Object.keys(next).forEach((cusId) => {
				const entry = next[cusId];
				if (!entry) { delete next[cusId]; return; }
				const remaining = (entry.sites || []).filter((s) => !toRemove.has(customerSiteKey(cusId, s.site_id)));
				if (remaining.length === 0) delete next[cusId];
				else next[cusId] = { ...entry, sites: remaining };
			});
			return next;
		});

		setSelectedSitesToMove([]);
		setSiteSelected([]);
	}, [selectedSitesToMove]);

	const canMoveRight = useMemo(() => {
		if (!customerSelected || siteSelected.length === 0) return false;
		if (disableAllSelection) return false;
		if (
			singleCustomer &&
			assignedSummary.totalCustomers >= 1 &&
			!sameId(customerSelected.cus_id, assignedSummary.firstCustomerId)
		) return false;
		if (singleSitePerCustomer) {
			const custId = toIdKey(customerSelected.cus_id);
			if ((selectedSites[custId]?.sites || []).length >= 1) return false;
		}
		return true;
	}, [customerSelected, siteSelected, disableAllSelection, singleCustomer, assignedSummary, selectedSites, singleSitePerCustomer]);

	// ── Render ────────────────────────────────────────────────────────────────
	if (!initializedRef.current) return <ListboxesSkeleton />;

	return (
		<div className="user-cus-site-selection">

			{/* ── Column 1: Customer list ──────────────────────────────────── */}
			<div className="list-box">
				<Box pb={2}>
					<ContextFilterAndSort
						value={customerSearchTerm}
						onSearchChange={setCustomerSearchTerm}
						placeholder="Search Customers"
						showSearch
						showSort={false}
					/>
				</Box>
				<div
					className="list-box-container"
					style={disableAllSelection ? { opacity: 0.5, pointerEvents: "none" } : {}}
				>
					{filteredCustomers.map((item, index) => {
						const disabled =
							singleCustomer &&
							assignedSummary.totalCustomers >= 1 &&
							!sameId(item.cus_id, assignedSummary.firstCustomerId);
						return (
							<div
								key={item?.cus_id ?? index}
								className={`list-box-item ${customerSelected === item ? "selected" : ""}`}
								style={disabled ? { opacity: 0.5, pointerEvents: "none" } : {}}
								onClick={() => handleCustomerListItemClick(item)}
							>
								<p className="title">{item.customer_name}</p>
								<p className="description">{item.customer_number || item.customer_id}</p>
							</div>
						);
					})}
				</div>
			</div>

			{/* ── Column 2: Available sites ────────────────────────────────── */}
			<div className="list-box">
				<Box pb={2}>
					<ContextFilterAndSort
						value={sitesSearchTerm}
						onSearchChange={setSitesSearchTerm}
						placeholder={t("GENERAL.SEARCH_CUSTOMER_LOCATIONS")}
						showSearch
						showSort={false}
					/>
				</Box>
				<div
					className="list-box-container"
					style={disableAllSelection ? { opacity: 0.5, pointerEvents: "none" } : {}}
				>
					<div className="list-box-item-header">
						<p className="title">
							{customerSelected
								? `${customerSelected.customer_number || customerSelected.customer_id || customerSelected.cus_id} - ${customerSelected.customer_name}`
								: t("UI_TEXT.PLEASE_SELECT_CUSTOMER")}
						</p>
					</div>

					{customerSelected && availableSitesFiltered.map((item, index) => (
						<div
							key={item?.site_id ?? index}
							className={`list-box-item ${siteSelected.includes(item) ? "selected" : ""}`}
							onClick={() => handleSiteListItemClick(item)}
						>
							<p className="title">{item.customer_site_name}</p>
							<p
								className="description"
								title={item.address_name_ln
									? `${item.customer_site_number} - ${item.address_name_ln}`
									: item.customer_site_number}
							>
								{item.customer_site_number}
								{item.address_name_ln ? ` - ${truncateWords(item.address_name_ln)}` : ""}
							</p>
						</div>
					))}
				</div>
			</div>

			{/* ── Column 3: Chevron controls ───────────────────────────────── */}
			<div className="list-controls">
				<Button variant="outlined" color="primary" onClick={handleMoveToSelectedSites} disabled={!canMoveRight}>
					<ChevronRight />
				</Button>
				<Button variant="outlined" color="primary" onClick={handleMoveToFilteredSites} disabled={selectedSitesToMove.length === 0}>
					<ChevronLeft />
				</Button>
			</div>

			{/* ── Column 4: Assigned sites — grouped by customer ───────────── */}
			<div className="list-box">
				<div className="list-box-container" style={{ minHeight: "490px" }}>
					{Object.keys(selectedSites).map((bucketKey) => {
						const entry = selectedSites[bucketKey];
						if (!entry) return null;

						// Prefer the live customer object for display accuracy.
						// Fall back to whatever was stored (synthetic object post-save).
						const liveCustomer = customerById.get(bucketKey) || null;
						const displayCustomer = liveCustomer || entry.customer;
						const displayNumber =
							displayCustomer?.customer_number ||
							displayCustomer?.customer_id ||
							bucketKey;
						const displayName =
							displayCustomer?.customer_name || `Customer ${bucketKey}`;

						return (
							<React.Fragment key={bucketKey}>
								{/* Customer group header: "1001 – Alpenrose Dairy" */}
								<div className="list-box-item-header">
									<p className="title">{displayNumber} – {displayName}</p>
								</div>

								{/* Sites that belong to THIS customer only */}
								{(entry.sites || []).map((site) => (
									<div
										key={site.site_id}
										className={`list-box-item ${selectedSitesToMove.includes(site) ? "selected" : ""}`}
										onClick={() => handleSelectedSiteClick(site)}
									>
										<p className="title">{site.customer_site_name}</p>
										<p
											className="description"
											title={site.address_name_ln
												? `${site.customer_site_number} - ${site.address_name_ln}`
												: site.customer_site_number}
										>
											{site.customer_site_number}
											{site.address_name_ln ? ` - ${truncateWords(site.address_name_ln)}` : ""}
										</p>
									</div>
								))}
							</React.Fragment>
						);
					})}
				</div>
			</div>

		</div>
	);
};

export default Listboxes;
